import React from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react';
import ImageUploadField from './ImageUploadField';

const SectionsEditor = ({ sections = [], onChange, folder = 'sections' }) => {
  const update = (index, field, value) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const addSection = () => {
    const id = 'sec_' + Date.now();
    onChange([
      ...sections,
      {
        id,
        title: '',
        body: '',
        image_url: '',
        image_position: sections.length % 2 === 0 ? 'right' : 'left',
        order: sections.length,
        is_active: true,
        links: [],
        cta_label: '',
        cta_url: '',
      },
    ]);
  };

  const removeSection = (index) => {
    const updated = sections.filter((_, i) => i !== index);
    onChange(updated);
  };

  const moveSection = (index, direction) => {
    const updated = [...sections];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= updated.length) return;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((s, i) => (s.order = i));
    onChange(updated);
  };

  const addLink = (sectionIndex) => {
    const updated = [...sections];
    const links = updated[sectionIndex].links || [];
    updated[sectionIndex] = {
      ...updated[sectionIndex],
      links: [...links, { label: '', url: '', icon: '' }],
    };
    onChange(updated);
  };

  const updateLink = (sectionIndex, linkIndex, field, value) => {
    const updated = [...sections];
    const links = [...(updated[sectionIndex].links || [])];
    links[linkIndex] = { ...links[linkIndex], [field]: value };
    updated[sectionIndex] = { ...updated[sectionIndex], links };
    onChange(updated);
  };

  const removeLink = (sectionIndex, linkIndex) => {
    const updated = [...sections];
    const links = (updated[sectionIndex].links || []).filter((_, i) => i !== linkIndex);
    updated[sectionIndex] = { ...updated[sectionIndex], links };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Content Sections</h3>
        <button
          onClick={addSection}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          data-testid="add-section-btn"
        >
          <Plus size={14} /> Add Section
        </button>
      </div>
      <p className="text-xs text-gray-400">
        Each section shows text with an optional image. Images alternate left/right automatically.
      </p>

      {sections.map((section, i) => (
        <div
          key={section.id || i}
          className={`border rounded-lg p-5 space-y-4 ${section.is_active === false ? 'opacity-60 bg-gray-50' : 'bg-white'}`}
          data-testid={`section-editor-${i}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-500">
              Section {i + 1}{section.title ? `: ${section.title.substring(0, 40)}...` : ''}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => update(i, 'is_active', !section.is_active)} className="p-1.5 text-gray-400 hover:text-gray-600" title={section.is_active !== false ? 'Hide' : 'Show'}>
                {section.is_active !== false ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ChevronUp size={14} />
              </button>
              <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                <ChevronDown size={14} />
              </button>
              <button onClick={() => removeSection(i)} className="p-1.5 text-red-400 hover:text-red-600">
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => update(i, 'title', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Section title"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Body <span className="text-gray-400">(separate paragraphs with blank lines)</span>
            </label>
            <textarea
              value={section.body || ''}
              onChange={(e) => update(i, 'body', e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Section body text..."
            />
          </div>

          {/* Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImageUploadField
              value={section.image_url || ''}
              onChange={(url) => update(i, 'image_url', url)}
              folder={folder}
              label="Section Image (optional)"
            />
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Image Position</label>
              <select
                value={section.image_position || 'right'}
                onChange={(e) => update(i, 'image_position', e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="right">Right of text</option>
                <option value="left">Left of text</option>
              </select>
            </div>
          </div>

          {/* CTA Button */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">CTA Button Label (optional)</label>
              <input
                type="text"
                value={section.cta_label || ''}
                onChange={(e) => update(i, 'cta_label', e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="e.g., Inquire Now"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">CTA Button URL</label>
              <input
                type="text"
                value={section.cta_url || ''}
                onChange={(e) => update(i, 'cta_url', e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="e.g., /inquire"
              />
            </div>
          </div>

          {/* Links */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-500">External Links (optional)</label>
              <button
                onClick={() => addLink(i)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                + Add Link
              </button>
            </div>
            {(section.links || []).map((link, li) => (
              <div key={li} className="flex items-center gap-2 mb-2">
                <select
                  value={link.icon || ''}
                  onChange={(e) => updateLink(i, li, 'icon', e.target.value)}
                  className="w-24 px-2 py-1.5 border rounded text-xs"
                >
                  <option value="">No icon</option>
                  <option value="instagram">Instagram</option>
                  <option value="website">Website</option>
                </select>
                <input
                  type="text"
                  value={link.label || ''}
                  onChange={(e) => updateLink(i, li, 'label', e.target.value)}
                  className="flex-1 px-2 py-1.5 border rounded text-xs"
                  placeholder="Label"
                />
                <input
                  type="text"
                  value={link.url || ''}
                  onChange={(e) => updateLink(i, li, 'url', e.target.value)}
                  className="flex-1 px-2 py-1.5 border rounded text-xs"
                  placeholder="https://..."
                />
                <button onClick={() => removeLink(i, li)} className="p-1 text-red-400 hover:text-red-600">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {sections.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400">
          <p className="text-sm">No content sections yet.</p>
          <button onClick={addSection} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
            Add your first section
          </button>
        </div>
      )}
    </div>
  );
};

export default SectionsEditor;
